import React from 'react';
import { cn } from '../utils/cn';

declare const __APP_VERSION__: string;

export type NavTabId = 'dashboard' | 'syllabus' | 'planner' | 'timer' | 'analytics' | 'settings';

export interface NavigationShellProps {
  activeTab: NavTabId;
  onTabChange: (tab: NavTabId) => void;
  candidateName?: string;
  candidateId?: string;
  isSyncing?: boolean;
  children: React.ReactNode;
}

interface NavItemDef {
  id: NavTabId;
  label: string;
  icon: string;
}

const NAV_ITEMS: NavItemDef[] = [
  { id: 'dashboard', label: 'Dashboard', icon: 'home' },
  { id: 'timer', label: 'Timer', icon: 'timer' },
  { id: 'syllabus', label: 'Syllabus', icon: 'menu_book' },
  { id: 'analytics', label: 'Analytics', icon: 'insert_chart' },
  { id: 'planner', label: 'Planner', icon: 'event_note' },
  { id: 'settings', label: 'Settings', icon: 'settings' },
];

const COMPACT_NAV_ITEMS = NAV_ITEMS.filter((item) => item.id !== 'settings');

export const NavigationShell: React.FC<NavigationShellProps> = ({
  activeTab,
  onTabChange,
  candidateName = 'Student',
  candidateId = '',
  isSyncing = false,
  children,
}) => {
  return (
    <div className="min-h-screen bg-[#0E0E0E] text-[#E5E2E1] flex flex-col md:flex-row antialiased selection:bg-[#8AB4F8]/30 selection:text-[#8AB4F8]">
      {/* Desktop Lateral Navigation Rail */}
      <aside className="hidden md:flex flex-col justify-between w-64 bg-[#161616] border-r border-[rgba(255,255,255,0.05)] p-6 shrink-0 z-40 h-screen sticky top-0">
        <div className="flex flex-col gap-6">
          {/* Header Brand */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="font-['Space_Grotesk'] text-sm tracking-wider text-[#8AB4F8] font-semibold">
                [ A/L CORE ]
              </span>
              <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#201F1F]">
                <span className={cn('w-1.5 h-1.5 rounded-full', isSyncing ? 'bg-[#FDD663] animate-ping' : 'bg-[#81C995] animate-pulse')} />
                <span className="font-['Space_Grotesk'] text-[10px] text-[#81C995] tracking-tight">
                  {isSyncing ? 'Syncing...' : 'Local DB'}
                </span>
              </div>
            </div>
            <span className="font-['Plus_Jakarta_Sans'] text-base font-semibold text-[#E5E2E1] truncate">
              {candidateName}
            </span>
            {candidateId && <span className="font-['Space_Grotesk'] text-xs text-[#8D919B]">{candidateId}</span>}
          </div>

          {/* Navigation Links */}
          <nav className="flex flex-col gap-2">
            {NAV_ITEMS.map((item) => {
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onTabChange(item.id)}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'motion-feedback flex items-center gap-3 px-4 py-3 rounded-full text-sm font-medium text-left',
                    isActive
                      ? 'bg-[#201F1F] text-[#8AB4F8] font-semibold border border-[rgba(138,180,248,0.2)] shadow-sm'
                      : 'text-[#C3C6D1] hover:text-[#E5E2E1] hover:bg-[#1A1A1A]'
                  )}
                >
                  <span className="material-symbols-outlined text-[20px]">{item.icon}</span>
                  <span className="font-['Plus_Jakarta_Sans']">{item.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Footer info */}
        <div className="text-[11px] font-['Space_Grotesk'] text-[#8D919B]/70 flex flex-col gap-1 border-t border-[rgba(255,255,255,0.05)] pt-4">
          <span>A/L Core Studio v{__APP_VERSION__.replace(/\.0$/, '')}</span>
          <span>G.C.E. A/L Study Tracker</span>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col min-w-0 pb-24 md:pb-8">
        {/* Mobile Header Bar */}
        <header className="md:hidden sticky top-0 z-30 bg-[#0E0E0E]/90 backdrop-blur-md border-b border-[rgba(255,255,255,0.05)] px-4 py-3 flex items-center justify-between">
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="font-['Space_Grotesk'] text-xs text-[#8AB4F8] font-bold">[ A/L CORE ]</span>
              <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#201F1F]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#81C995] animate-pulse" />
                <span className="font-['Space_Grotesk'] text-[9px] text-[#81C995]">Local</span>
              </div>
            </div>
            <span className="font-['Plus_Jakarta_Sans'] text-sm font-semibold truncate">{candidateName}</span>
          </div>
          <button
            type="button"
            onClick={() => onTabChange('settings')}
            className="motion-feedback w-9 h-9 rounded-full bg-[#201F1F] flex items-center justify-center text-[#C3C6D1] hover:text-white"
          >
            <span className="material-symbols-outlined text-[18px]">tune</span>
          </button>
        </header>

        {/* Screen Slot */}
        <div className="flex-1 p-4 md:p-8 max-w-7xl w-full mx-auto">
          <div key={activeTab} className="page-enter">
            {children}
          </div>
        </div>
      </main>

      {/* Mobile Bottom Navigation Bar */}
      <nav className="md:hidden fixed bottom-0 w-full z-50 bg-[#131313]/90 backdrop-blur-xl border-t border-[rgba(255,255,255,0.08)] pb-safe">
        <div className="flex items-center justify-around h-16 px-2 max-w-md mx-auto">
          {COMPACT_NAV_ITEMS.map((item) => {
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onTabChange(item.id)}
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                    'motion-feedback flex flex-col flex-1 min-w-0 items-center justify-center gap-1 h-12 rounded-full',
                  isActive
                    ? 'text-[#8AB4F8] bg-[#201F1F]'
                    : 'text-[#C3C6D1] hover:text-[#E5E2E1]'
                )}
              >
                <span className="material-symbols-outlined text-[20px]">{item.icon}</span>
                <span className="font-['Space_Grotesk'] text-[10px] tracking-tight">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};
